import { createContext, useState } from "react";
import classNames from "classnames";
import style from "./App.module.css";
import { unescape } from "lodash";
import Input from "./components/Input/Input";
import FileInput from "./components/FileInput/FileInput";
import Textarea from "./components/Textarea/Textarea";
import Dropdown from "./components/Dropdown/Dropdown";
import PhoneInput from "./components/PhoneInput/PhoneInput";
import LocationInput from "./components/LocationInput/LocationInput";
import GDPRConsent from "./components/GDPRConsent/GDPRConsent";
import { Control, SubmitHandler, useForm } from "react-hook-form";
import Button from "./components/Button/Button";
import { useMutation, useQuery } from "react-query";
import ErrorMessage from "./components/ErrorMessage/ErrorMessage";
import SuccessScreen from "./components/SuccessScreen/SuccessScreen";
import ErrorScreen from "./components/ErrorScreen/ErrorScreen";
import Image1 from "./assets/image_1.webp";
import Image2 from "./assets/image_2.webp";
import Image3 from "./assets/image_3.webp";

const origin = "https://d4pysoljc4vzs.cloudfront.net";

const addURI = (path: string) => "https://d4pysoljc4vzs.cloudfront.net" + path;

interface WidgetValue {
  label: string;
  value: number;
}

interface Widget {
  title: string;
  path: string;
  type: string;
  selectableValues: WidgetValue[];
}

interface Question {
  isRequired: boolean;
  field: Widget;
}

interface IFormContext {
  register: (name: string, options?: any) => any;
  control: Control<any>;
  errors: any;
}

export const FormContext = createContext<IFormContext>({
  register: () => {},
  control: null!,
  errors: {},
});

const mandatoryFields = ["Name", "Email", "Resume", "Cover Letter"];

//We have a special link format due to linkedin not handling links:
//in greenhouse every link will be written like this: "... click here (https://foo.bar) ..."
//in linkedin this will appear as is but for our own page we will parse this and replace the
//first word before the link in parentheses with the appropriate link tag and remove the written out link
//eg.: "... click here (https://foo.bar) ..." ---> "... click <a href="https://foo.bar">here</a> ..."
const replaceIndex = (content: string) => {
  //const linkMiddleIndex = content.indexOf(" (http");
  const linkMiddleIndex = content.indexOf(" (<a");
  if (linkMiddleIndex === -1) return content;
  const linkEndIndex = content.indexOf("/a>)", linkMiddleIndex);
  const linkStartIndex = content.lastIndexOf(" ", linkMiddleIndex - 1) + 1;
  const linkText = content.substr(
    linkStartIndex,
    linkMiddleIndex - linkStartIndex,
  );
  const linkHREFStart = content.indexOf('href="', linkMiddleIndex);
  const linkHREFEnd = content.indexOf('"', linkHREFStart + 7);
  const linkURL = content.substr(
    linkHREFStart + 6,
    linkHREFEnd - linkHREFStart - 6,
  );
  return (
    content.slice(0, linkStartIndex) +
    `<a href="${linkURL}" target="_blank" rel="noopener noreferrer" >${linkText}</a>` +
    content.slice(linkEndIndex + 4)
  );
};

const CITY_TO_COUNTRY_MAP: Record<string, string> = {
  Budapest: "Hungary",
  Denver: "United States",
};

function App() {
  const [content, setContent] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [jobTitle, setJobTitle] = useState("");
  const [locationName, setLocationName] = useState("Budapest");

  const jobId = new URLSearchParams(window.location.search).get("id");
  const parseHTMLString = (() => {
    const parser = new DOMParser();
    return (str: string) => parser.parseFromString(str, "text/html");
  })();

  const {
    isLoading,
    isError: isLoadingError,
    data,
  } = useQuery(
    ["fetchJobDetails", jobId],
    async () => {
      const response = await fetch(
        `https://8q2z5k9ah4.execute-api.eu-central-1.amazonaws.com/default/shapr3d-ashby-proxy/info`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({ jobPostingId: jobId }),
        },
      );
      if (!response.ok) {
        throw new Error("Something went wrong");
      }
      return response.json();
    },
    {
      enabled: jobId !== null,
      onSuccess: (data) => {
        let content = unescape(data.results.descriptionHtml);
        content = content
          .replace("<em>", "<em><span>")
          .replace("</em>", "</span></em>");

        const strongTag =
          parseHTMLString(content).querySelector("em > span > strong");

        // Find the name of the person and remove special characters
        // -&nbsp;Márton Bognár, Quality Assurance Lead --> MrtonBognr
        const imageKey = strongTag?.innerHTML
          .replace(/&nbsp;/g, "")
          .split(",")[0]
          .split("-")[1]
          ?.replace(/[^a-zA-Z]/g, "");

        let injectedHTMLString = `</em><div class="${style.mobileQuoteContainer}"><div>${strongTag?.innerHTML}</div></div>`;
        if (imageKey) {
          const imageElement = document.createElement("img");
          imageElement.src = `${origin}/images/${imageKey}.png?t=${performance.now()}`;
          imageElement.classList.add(style.quoteImageDesktop);
          injectedHTMLString = imageElement.outerHTML + injectedHTMLString;
          imageElement.classList.remove(style.quoteImageDesktop);
          imageElement.classList.add(style.quoteImageMobile);
          injectedHTMLString = injectedHTMLString.replace(
            `<div class="${style.mobileQuoteContainer}">`,
            `<div class="${style.mobileQuoteContainer}">${imageElement.outerHTML}`,
          );
        }

        let replacedContent = replaceIndex(content);
        while (replacedContent !== content) {
          content = replacedContent;
          replacedContent = replaceIndex(content);
        }

        setContent(content.replace("</em>", injectedHTMLString));
        setQuestions(data.results.applicationFormDefinition.sections[0].fields);
        setJobTitle(data.results.title);
        setLocationName(data.results.locationName);
        document.title = data.results.title;
      },
    },
  );

  const {
    mutate: sendApplication,
    error,
    isLoading: isSubmitInProgress,
    isSuccess: isSubmitted,
  } = useMutation(
    async (data: FormData) => {
      const response = await fetch(
        `https://8q2z5k9ah4.execute-api.eu-central-1.amazonaws.com/default/shapr3d-ashby-proxy/submit`,
        {
          method: "POST",
          body: data,
        },
      );
      if (!response.ok) {
        throw new Error("Something went wrong");
      }
      const json = await response.json();
      if (!json.success) {
        throw new Error("Something went wrong");
      }
      return json;
    },
    {
      onSuccess: () => {
        window.dataLayer?.push?.({
          event: "GAEvent",
          eventCategory: "Jobs",
          eventAction: "Form Submit",
          eventLabel: window.location.href,
        });
      },
    },
  );
  const formErrorText = error ? "Something went wrong" : "";

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>();

  const onSubmit: SubmitHandler<any> = async (data: any) => {
    const formData = new FormData();
    formData.append("jobPostingId", jobId!);

    const formFields = [];
    for (const [path, value] of Object.entries(data)) {
      if (value) {
        if (value instanceof File) {
          const filename = "file" + performance.now();
          formData.append(filename, value);
          formFields.push({ path, value: filename });
        } else {
          formFields.push({ path, value });
        }
      }
    }
    formData.append(
      "applicationForm",
      JSON.stringify({ fieldSubmissions: formFields }),
    );

    // Referral tracking, exmaple url:
    // https://www.shapr3d.com/career/job-details?id=b2c6225d-827c-48d4-a7ed-f6f39c231413&utm_source=oGENAy515r
    const utmSource = new URLSearchParams(window.location.search).get(
      "utm_source",
    );
    if (utmSource) {
      formData.append("utmData", JSON.stringify({ utm_source: utmSource }));
    }

    sendApplication(formData);
  };

  if (isLoading) {
    return null;
  }

  if (isLoadingError || jobId === null) {
    return <ErrorScreen />;
  }

  if (isSubmitted) {
    return <SuccessScreen />;
  }

  return (
    <div className={style.container}>
      <div className={style.jobDetailsContainer}>
        <div
          className={classNames(style.jobTitleSection, "color-panel")}
          data-color="darkblue"
        >
          <div className={style.departmentName}>
            {data.results.departmentName}
          </div>
          {false && (
            <div className={style.jobLocation}>
              {locationName}, {CITY_TO_COUNTRY_MAP[locationName]}
            </div>
          )}
          <div className={style.jobTitle}>{jobTitle}</div>
          {
            <Button
              className={style.applyButton}
              variant="secondary"
              onClick={() =>
                document
                  .getElementById(style.jobsFormScrollTarget)
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Apply now
            </Button>
          }
        </div>
        <div
          className={classNames(style.jobDescriptionContainer, "color-panel")}
          data-color="white"
        >
          <div dangerouslySetInnerHTML={{ __html: content }}></div>
        </div>
        <div className={style.imageContainer}>
          <img src={addURI(Image1)} alt="Shapr3D team" />
          <img src={addURI(Image2)} alt="Shapr3D team" />
          <img src={addURI(Image3)} alt="Shapr3D team" />
        </div>
      </div>
      <FormContext.Provider value={{ register, control, errors }}>
        <div id={style.jobsFormScrollTarget} />
        <div className={classNames(style.formContainer)}>
          <form id="jobs-form" onSubmit={handleSubmit(onSubmit)}>
            <div className={style.titleSection}>
              <div className={style.formTitle}>Apply for this job.</div>
              <div className={style.subtitleContainer}>
                <div className={style.subtitle}>
                  Please submit your CV in English.
                </div>
                {false && (
                  <a href="todo" className={style.shareLink}>
                    Share this post
                  </a>
                )}
              </div>
            </div>
            <div className={style.formSection}>
              <div>
                <div className={style.formRow}>
                  <Input label="Name" name="_systemfield_name" required />
                </div>
                <div className={style.formRow}>
                  <Input
                    label="Email"
                    name="_systemfield_email"
                    required
                    type="email"
                  />
                </div>
                <div className={style.formRow}>
                  <FileInput
                    label="Resume/CV"
                    name="_systemfield_resume"
                    required
                  />
                  <FileInput label="Cover Letter" name="cover_letter" />
                </div>
              </div>
              <div className={style.customFieldsContainer}>
                {questions &&
                  questions
                    .filter(
                      ({ field: { title } }) =>
                        !mandatoryFields.includes(title),
                    )
                    .map((question) => {
                      const {
                        type,
                        title: label,
                        selectableValues: values,
                        path: name,
                      } = question.field;
                      if (type === "String") {
                        return (
                          <Input
                            key={name}
                            name={name}
                            label={label}
                            required={question.isRequired}
                          />
                        );
                      }
                      if (type === "Location") {
                        return (
                          <LocationInput
                            key={name}
                            name={name}
                            label={label}
                            required={question.isRequired}
                          />
                        );
                      }
                      if (type === "Phone") {
                        return (
                          <PhoneInput
                            key={name}
                            name={name}
                            label={label}
                            required={question.isRequired}
                          />
                        );
                      }
                      if (type === "File") {
                        return (
                          <FileInput
                            key={name}
                            name={name}
                            label={label}
                            required={question.isRequired}
                          />
                        );
                      }
                      if (type === "LongText") {
                        return (
                          <Textarea
                            key={name}
                            name={name}
                            label={label}
                            required={question.isRequired}
                          />
                        );
                      }
                      if (
                        type === "ValueSelect" ||
                        type === "MultiValueSelect"
                      ) {
                        if (label === "GDPR & DATA PRIVACY CONSENT") {
                          return (
                            <GDPRConsent
                              key={name}
                              name={name}
                              options={values}
                            />
                          );
                        }
                        return (
                          <Dropdown
                            key={name}
                            name={name}
                            label={label}
                            options={values}
                            required={question.isRequired}
                          />
                        );
                      }
                      return (
                        <div key={label}>Error: Unknown widget: {label}</div>
                      );
                    })}
              </div>
              <Button type="submit" disabled={isSubmitInProgress}>
                {isSubmitInProgress ? "Submitting..." : "Submit application"}
              </Button>
              <ErrorMessage errorText={formErrorText} />
            </div>
          </form>
        </div>
      </FormContext.Provider>
    </div>
  );
}

export default App;
